import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import { HeartIcon, ChatBubbleLeftRightIcon, UserPlusIcon, UserGroupIcon } from '@heroicons/react/24/solid';

const ACTIVITY_TYPES = {
  prayer: { label: 'Prayed for a disciple', icon: HeartIcon, color: 'text-purple-600', bg: 'bg-purple-100' },
  connection: { label: 'Connected with a disciple', icon: ChatBubbleLeftRightIcon, color: 'text-blue-600', bg: 'bg-blue-100' },
  message: { label: 'Connected with a disciple', icon: ChatBubbleLeftRightIcon, color: 'text-blue-600', bg: 'bg-blue-100' },
  in_person_meeting: { label: 'Connected with a disciple', icon: ChatBubbleLeftRightIcon, color: 'text-blue-600', bg: 'bg-blue-100' },
  joined: { label: 'Joined the organization', icon: UserPlusIcon, color: 'text-amber-600', bg: 'bg-amber-100' },
  member_joined: { label: 'Joined the organization', icon: UserPlusIcon, color: 'text-amber-600', bg: 'bg-amber-100' },
  disciple_added: { label: 'Added a new disciple', icon: UserGroupIcon, color: 'text-green-600', bg: 'bg-green-100' },
};

function formatDate(timestamp) {
  if (!timestamp) return '';
  const d = timestamp?.toDate ? timestamp.toDate() : (timestamp?.seconds ? new Date(timestamp.seconds * 1000) : new Date());
  const now = new Date();
  const diffMs = now - d;
  if (diffMs < 60000) return 'Just now';
  if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
  if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
  if (diffMs < 604800000) return `${Math.floor(diffMs / 86400000)}d ago`;
  return d.toLocaleDateString();
}

export default function OrganizationActivityFeed({ orgId }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const activityFeed = [];
        const activitiesRef = collection(db, 'organizations', orgId, 'activities');
        const activitiesSnap = await getDocs(query(activitiesRef, orderBy('createdAt', 'desc'), limit(150)));
        for (const activityDoc of activitiesSnap.docs) {
          const data = activityDoc.data();
          const type = data.type;
          if (!type || !['prayer', 'message', 'in_person_meeting', 'disciple_added'].includes(type)) continue;
          const displayType = type === 'message' || type === 'in_person_meeting' ? 'connection' : type;
          activityFeed.push({
            id: activityDoc.id,
            type: displayType,
            userId: data.userId || '',
            userName: data.userName || 'Unknown',
            userPhotoURL: data.userPhotoURL,
            discipleName: data.discipleName,
            createdAt: data.createdAt,
          });
        }
        const membersRef = collection(db, 'organizations', orgId, 'members');
        const membersSnap = await getDocs(membersRef);
        const orgDoc = await getDoc(doc(db, 'organizations', orgId));
        const orgData = orgDoc.exists() ? orgDoc.data() : {};
        for (const memberDoc of membersSnap.docs) {
          const memberData = memberDoc.data();
          const joinedAt = memberData.joinedAt;
          if (!joinedAt) continue;
          const userSnap = await getDoc(doc(db, 'users', memberDoc.id));
          const userData = userSnap.exists() ? userSnap.data() : {};
          activityFeed.push({
            id: `join_${memberDoc.id}`,
            type: 'joined',
            userId: memberDoc.id,
            userName: userData.name || 'Unknown',
            userPhotoURL: userData.photoURL,
            createdAt: joinedAt,
          });
        }
        const creatorId = orgData.createdBy ? (typeof orgData.createdBy === 'string' ? orgData.createdBy : orgData.createdBy?.uid || orgData.createdBy?.id) : orgData.creatorId;
        if (creatorId && !membersSnap.docs.find(d => d.id === creatorId)) {
          const userSnap = await getDoc(doc(db, 'users', creatorId));
          const userData = userSnap.exists() ? userSnap.data() : {};
          activityFeed.push({
            id: `join_${creatorId}`,
            type: 'joined',
            userId: creatorId,
            userName: userData.name || 'Unknown',
            userPhotoURL: userData.photoURL,
            createdAt: orgData.createdAt || { toDate: () => new Date() },
          });
        }
        activityFeed.sort((a, b) => {
          const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt?.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(0));
          const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(0));
          return bDate.getTime() - aDate.getTime();
        });
        if (!cancelled) setActivities(activityFeed.slice(0, 100));
      } catch (err) {
        console.error('Error loading activity feed:', err);
        if (!cancelled) setActivities([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [orgId]);

  if (loading) {
    return (
      <div className="p-4">
        <p className="text-gray-500 text-sm">Loading activity…</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Activity Feed</h2>
      <p className="text-gray-500 text-sm mb-4">Prayed, commented, joined</p>
      {activities.length === 0 ? (
        <p className="text-gray-500">No activity yet.</p>
      ) : (
        <ul className="space-y-3">
          {activities.map((activity) => {
            const meta = ACTIVITY_TYPES[activity.type] || ACTIVITY_TYPES.connection;
            const IconComponent = meta.icon;
            const desc = activity.discipleName && (activity.type === 'prayer' || activity.type === 'connection')
              ? (activity.type === 'prayer' ? `prayed for ${activity.discipleName}` : `connected with ${activity.discipleName}`)
              : meta.label;
            return (
              <li key={activity.id} className="flex items-start gap-3 p-3 rounded-lg bg-white border border-gray-100">
                {activity.userPhotoURL ? (
                  <img src={activity.userPhotoURL} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
                    <IconComponent className={`w-5 h-5 ${meta.color}`} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{activity.userName}</span>
                    <span className="text-gray-600"> {desc}</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">{formatDate(activity.createdAt)}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
